import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Download, Users, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";

export default function WaitlistAdmin() {
  const [key, setKey] = useState(localStorage.getItem("vi_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (passcode) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/waitlist/admin`, { params: { key: passcode } });
      setData(data);
      setAuthed(true);
      localStorage.setItem("vi_admin_key", passcode);
    } catch (err) {
      setAuthed(false);
      localStorage.removeItem("vi_admin_key");
      toast.error(err.response ? formatApiErrorDetail(err.response?.data?.detail) : err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    load(key.trim());
  };

  const exportCsv = () => {
    if (!data?.signups?.length) return;
    const rows = [["Email", "Collection", "Joined"], ...data.signups.map((s) => [s.email, s.category_label, s.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `vintage-identity-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="relative z-10 min-h-screen">
        <Navbar />
        <div className="px-6 py-24 flex justify-center">
          <form onSubmit={submit} data-testid="admin-login" className="w-full max-w-sm">
            <div className="flex flex-col items-center text-center mb-8">
              <span className="w-12 h-12 rounded-full bg-[#F4F1EA] border border-[#1A1A1A]/10 flex items-center justify-center mb-4">
                <Lock className="w-5 h-5 text-[#6D214F]" />
              </span>
              <h1 className="font-serif-display text-3xl">Waitlist</h1>
              <p className="text-sm text-[#4A4A4A] mt-2">Enter your passcode to view signups.</p>
            </div>
            <input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Passcode"
              className="w-full bg-[#F4F1EA] border border-[#1A1A1A]/10 rounded-full px-5 py-3.5 text-sm text-center focus:outline-none focus:border-[#6D214F] transition-colors"
            />
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full mt-4 bg-[#1A1A1A] text-[#FDFBF7] py-3.5 rounded-full hover:bg-[#6D214F] transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? "Checking…" : "View signups"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen">
      <Navbar />
      <div className="px-6 md:px-16 py-12 max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#8B5A2B] mb-2">The Waitlist</p>
            <h1 className="font-serif-display text-4xl flex items-center gap-3">
              <Users className="w-7 h-7 text-[#6D214F]" /> {data?.count || 0} interested
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh"
              onClick={() => load(key)}
              className="inline-flex items-center gap-2 border border-[#1A1A1A]/20 px-5 py-2.5 rounded-full text-sm hover:border-[#6D214F] hover:text-[#6D214F] transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              data-testid="admin-export"
              onClick={exportCsv}
              disabled={!data?.signups?.length}
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] px-5 py-2.5 rounded-full text-sm hover:bg-[#6D214F] transition-colors disabled:opacity-40"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {data?.by_category && Object.keys(data.by_category).length > 0 && (
          <div className="flex flex-wrap gap-3 mb-8">
            {Object.entries(data.by_category).map(([label, n]) => (
              <span key={label} className="rounded-full bg-[#F4F1EA] border border-[#1A1A1A]/10 px-4 py-2 text-sm text-[#4A4A4A]">
                {label} · <strong className="text-[#1A1A1A]">{n}</strong>
              </span>
            ))}
          </div>
        )}

        {data?.signups?.length ? (
          <div className="overflow-hidden rounded-2xl border border-[#1A1A1A]/10">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="bg-[#F4F1EA] text-left text-xs uppercase tracking-[0.2em] text-[#8B5A2B]">
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Collection</th>
                  <th className="px-5 py-3">Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.signups.map((s, i) => (
                  <motion.tr
                    key={`${s.email}-${s.category}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.02 }}
                    className="border-t border-[#1A1A1A]/8"
                  >
                    <td className="px-5 py-3.5 text-[#1A1A1A]">{s.email}</td>
                    <td className="px-5 py-3.5 text-[#4A4A4A]">{s.category_label}</td>
                    <td className="px-5 py-3.5 text-[#4A4A4A]">{new Date(s.created_at).toLocaleString()}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="font-serif-display text-2xl italic text-[#8B5A2B]">No signups yet.</p>
        )}
      </div>
    </div>
  );
}
